/* eslint-disable no-unused-vars */
/* eslint-disable default-case */

// ** Polyfills
import ResizeObserver from "resize-observer-polyfill";

import { Extension } from "./src";

// ** React Imports
import { useEffect, useRef, useState, useContext } from "react";

// ** Third Party Components
import { mergeRefs, useLayer } from "react-laag";
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import UserAvatar from "components/user/UserAvatar";

// ** Custom Hooks
import useDetectKeyboardOpen from "hooks/useDetectKeyboardOpen";
import useMobileDetect from "hooks/useMobileDetectHook";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Editor Components
import MentionDataItem from "./components/MentionDataItem";

// ** Utils & Helpers
import api from "helpers/ApiClient";

// ** Styled Components
import {
	StyledDropdownLayerContainer,
	StyledMotionDiv,
} from "components/layout/Dropdown/styles";

// ** Styles
import { MentionList, MentionInput } from "./style";

// #####################################################

const MENTION_TYPE = /@(user):([0-9]+)/i;

// #####################################################

export class MentionExtension extends Extension {
	render(dom) {
		const isAndroid = () => Boolean(navigator?.userAgent.match(/Android/i));
		const isIos = () =>
			Boolean(navigator?.userAgent.match(/iPhone|iPad|iPod/i));
		const isFirefox = () => Boolean(navigator?.userAgent.match(/Firefox/i));

		const mention = dom.create({
			element: "span",
			text: `@${this.data.login || this.data.name}`,
			attribute: {
				class: "mention",
				contenteditable: isAndroid() || isIos() ? true : false,
				"data-entity-type": "mention",
				"data-type": this.data.type,
				"data-name": this.data.login || this.data.name,
				"data-id": this.data.id,
			},
		});

		if (isAndroid() || isIos()) {
			const mutateObserver = new MutationObserver((records) => {
				if (records.length > 0) records[0].target?.remove();
			});
			mutateObserver.observe(mention, {
				childList: true, // capture child add/remove on target element.
				characterData: true, // capture text changes on target element
				characterDataOldValue: true, // keep of prev value
				subtree: true, // capture childs changes too
			});

			return dom.create({
				children: [
					{
						text: isFirefox() ? " \uFEFF" : "\uFEFF",
						attribute: {
							parent: mention,
							direction: "left",
						},
					},
					mention,
					{
						text: isFirefox() ? "\uFEFF " : "\uFEFF",
						attribute: {
							parent: mention,
							direction: "right",
						},
					},
				],
			});
		} else
			return dom.create({
				children: [
					mention,
					{
						text: "\uFEFF",
						attribute: {
							parent: mention,
							direction: "right",
						},
					},
				],
			});
	}

	serializeMentionFull() {
		return `@${this.data.login || this.data.name}`;
	}

	serialize() {
		return `@${this.data.type}:${this.data.id}`;
	}

	static type = "mention";
}

export function transform(line, handler) {
	const transformed = [];

	let buffer = line;
	let result = MENTION_TYPE.exec(buffer);

	if (result == null) {
		return line;
	}

	while (result) {
		if (result.index) {
			transformed.push(buffer.substring(0, result.index));
		}

		transformed.push(handler(result));

		buffer = buffer.substring(
			result.index + result[0].length,
			buffer.length
		);

		result = MENTION_TYPE.exec(buffer);

		if (!result && buffer.length) {
			transformed.push(buffer);
		}
	}

	return transformed;
}

export function useMentionContext({ enabled }) {
	const [context, setContext] = useState(() => {
		return {
			enabled,
			visible: false,

			selectedIndex: 0,
			result: [],

			set: (context = {}) => setContext(context),
			update(context = {}) {
				return setContext((current) => {
					if (typeof context === "function") {
						return {
							...current,
							...context(current),
						};
					}

					return {
						...current,
						...context,
					};
				});
			},
		};
	});

	return context;
}

const AvatarComponent = ({ mention }) => {
	if (mention.type === "user") {
		return (
			<UserAvatar
				userId={mention?.id}
				src={mention.avatar}
				login={mention.login}
				online={mention.online}
				privateData={mention.private_data}
				status={mention.profile_status}
				size="xxsmall"
			/>
		);
	}

	return null;
};

export const Mention = ({ editor, context, mention, index }) => {
	const { isPhone } = useContext(deviceTypeContext);

	const onMouseOver = () => {
		if (context.selectedIndex === index) {
			return;
		}

		context.update({
			selectedIndex: index,
		});
	};

	const preventDefault = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const onSelected = (e) => {
		e.preventDefault();
		e.stopPropagation();

		context.onSelected(mention);
	};

	useEffect(() => {
		const onKeyDown = (event) => {
			if (context.selectedIndex !== index) {
				return false;
			}

			if (event.key !== "Enter") {
				return false;
			}

			event.preventDefault();
			event.stopPropagation();

			context.onSelected(mention);

			return true;
		};

		editor.addHotkey(onKeyDown);

		return () => {
			editor.removeHotkey(onKeyDown);
		};
	}, [context.selectedIndex, context.onSelected]);

	if (mention.type === "noResults") {
		return (
			<Block flex style={{ margin: "0 auto" }}>
				<MentionDataItem mention={mention} />
			</Block>
		);
	}
	return (
		<a
			href="#"
			className={context.selectedIndex === index ? "selected" : ""}
			onClick={onSelected}
			onMouseOver={onMouseOver}
			onMouseDown={preventDefault}
			onMouseUp={preventDefault}
		>
			<Block flex>
				<AvatarComponent mention={mention} />
				<MentionDataItem mention={mention} isPhone={isPhone} />
			</Block>
		</a>
	);
};

const paramsForLayer = (context, initialTop, currentTop) => {
	const { innerWidth: windowWidth, innerHeight: windowHeight } = window;
	const position = context.position[0];
	const numberOfElements = context.result.length;
	const heightOfElement = 52;

	const mentionDimensions = {
		width: 210,
		height: 240 - (5 - numberOfElements) * heightOfElement + 70,
	};

	let left = position.left - 10;
	let top = position.top + currentTop - initialTop;

	if (position.x > windowWidth - mentionDimensions.width - 10) {
		// okno otwiera się w lewo
		left = position.left - mentionDimensions.width;
	}

	if (position.y > windowHeight / 2) {
		// okno otwiera się do góry
		top =
			position.top -
			10 -
			mentionDimensions.height +
			currentTop -
			initialTop;
	} else {
		// okno otwiera się do dołu
		top = position.top + 25 + currentTop - initialTop;
	}

	// Sprawdzenie, czy pozycja warstwy wychodzi poza ekran
	if (left < 0) {
		left = 10;
	} else if (left + mentionDimensions.width > windowWidth) {
		left = windowWidth - mentionDimensions.width;
	}

	return {
		positions: {
			left,
			top,
		},
	};
};

export function MentionHook({
	editor,
	context,
	editorHeight,
	innerRef,
	inModal,
}) {
	const { t } = useTranslation(["common"]);
	const { isKeyboardOpen, visualViewportHeight } = useDetectKeyboardOpen();
	const mobileDetect = useMobileDetect();

	const [mentionText, setMentionText] = useState(context.match);
	const [firstResults, setFirstResults] = useState([]);

	const triggerRef = useRef(null);
	const controllerRef = useRef(new AbortController());
	const mentionInputRef = useRef(null);

	const controller = controllerRef.current;
	const isIos = mobileDetect.isIos();

	useEffect(() => {
		api.post(
			`/datezone/suggest`,
			{ name: "a" },
			{
				signal: controllerRef.current.signal,
			}
		)
			.then((result) => {
				if (result.data) {
					setFirstResults(result.data || []);
					context.update({
						visible: true,
						result: result.data || [],
					});
				} else {
					context.update({
						visible: true,
						result: [
							{
								type: "noResults",
							},
						],
					});
				}
			})
			.catch((e) => {
				context.update({
					visible: true,
					result: [
						{
							type: "noResults",
						},
					],
				});
				if (e.message === "canceled") {
					return null;
				}

				// TODO: Error handler in extension.
				// eslint-disable-next-line no-console
				console.error(e);
			});
	}, []);

	// ** Hook useLayer - React Laag
	const { renderLayer, triggerProps, layerProps } = useLayer({
		onOutsideClick: () => {
			context.update({
				visible: false,
				result: [],
			});
			context.onClose(isIos);
		},
		onParentClose: () => {
			context.update({
				visible: false,
				result: [],
			});
			context.onClose(isIos);
		},
		isOpen: true,
		overflowContainer: true,
		auto: false,
		placement: "bottom-end",
		triggerOffset: -1,
		container: "app",
		ResizeObserver,
	});

	const isInitialTop = useRef(false);
	const [initialTop, setInitialTop] = useState(0);

	const calculatePosition = paramsForLayer(
		context,
		initialTop,
		layerProps.style.top
	);

	useEffect(() => {
		if (!isInitialTop.current && layerProps.style.top !== 0) {
			setInitialTop(layerProps.style.top);
			isInitialTop.current = true;
		}
	}, [layerProps.style.top]);

	// ** Style LayerPropsStyle - domyślne lub przekazane przez propsa 'customLayerStyle'.
	const layerPropsStyle = {
		position: "fixed",
		willChange: "top, left, width, height",
		zIndex: inModal ? 1001 : 499,
		overflow: "auto",
		overscrollBehaviorY: "contain",
		maxHeight: isKeyboardOpen ? `${visualViewportHeight - 10}px` : null,
		...layerProps.style,
		left: calculatePosition.positions.left,
		top: calculatePosition.positions.top,
	};

	useEffect(() => {
		const onLayerKeyDown = (event) => {
			if (event.key === "ArrowUp") {
				event.preventDefault();
				event.stopPropagation();

				context.update((current) => ({
					selectedIndex:
						current.selectedIndex - 1 < 0
							? current.result.length - 1
							: current.selectedIndex - 1,
				}));

				return true;
			}

			if (event.key === "ArrowDown") {
				event.preventDefault();
				event.stopPropagation();

				context.update((current) => ({
					selectedIndex:
						current.selectedIndex + 1 >= current.result.length
							? 0
							: current.selectedIndex + 1,
				}));

				return true;
			}
		};

		editor.addHotkey(onLayerKeyDown);

		return () => {
			editor.removeHotkey(onLayerKeyDown);
		};
	}, []);

	const handleMentionTextChange = (event) => {
		controller.abort();
		controllerRef.current = new AbortController();
		const searchName = event.target.value;
		setMentionText(searchName);

		if (searchName.trim()) {
			api.post(
				`/datezone/suggest`,
				{ name: searchName },
				{
					signal: controllerRef.current.signal,
				}
			)
				.then((result) => {
					if (result.data) {
						context.update({
							visible: true,
							result: result.data || [],
						});
					} else {
						context.update({
							visible: true,
							result: [
								{
									type: "noResults",
								},
							],
						});
					}
				})
				.catch((e) => {
					if (e.message === "canceled") {
						return null;
					}
					// TODO: Error handler in extension.
					// eslint-disable-next-line no-console
					console.error(e);
				});
		} else {
			context.update({
				visible: true,
				result: firstResults || [
					{
						type: "noResults",
					},
				],
			});
		}
	};

	const handleTextFieldKeyDown = (event) => {
		if (
			mentionText === "" &&
			(event?.keyCode === 8 || event?.key === "Backspace")
		) {
			event?.preventDefault();
			context.update({
				visible: false,
				result: [],
			});
			context.onClose(isIos);
		}
	};

	useEffect(() => {
		const scrollTextFieldIntoView = () => {
			if (mentionInputRef.current) {
				mentionInputRef.current.scrollIntoView({ behavior: "auto" });
			}
		};

		const scrollTimeout = setTimeout(scrollTextFieldIntoView, 500);

		return () => {
			clearTimeout(scrollTimeout);
		};
	}, [mentionInputRef, visualViewportHeight, isKeyboardOpen]);

	return (
		<>
			{/* Funkcja Renderująca Aktywator */}
			<div
				ref={mergeRefs(triggerProps.ref, triggerRef)}
				{...triggerProps}
			></div>

			{/* Funkcja Renderująca Dropdown Container */}
			{renderLayer(
				<>
					<StyledMotionDiv
						innerRef={layerProps.ref}
						style={layerPropsStyle}
					>
						<StyledDropdownLayerContainer>
							<MentionList>
								{context.result.map((mention, index) => (
									<Mention
										key={`.${mention.type}-${mention.id}`}
										editor={editor}
										context={context}
										mention={mention}
										index={index}
									/>
								))}
							</MentionList>
							<Block p={2} pb={4}>
								<MentionInput
									ref={mentionInputRef}
									value={mentionText}
									onChange={handleMentionTextChange}
									onKeyDown={handleTextFieldKeyDown}
									label="label"
									placeholder={t("common:placeholder.write")}
									autoFocus
									type="search"
								/>
							</Block>
						</StyledDropdownLayerContainer>
					</StyledMotionDiv>
				</>
			)}
		</>
	);
}

export function MentionLayer({
	editor,
	context,
	editorHeight,
	innerRef,
	editorInternal,
	inModal,
}) {
	if (context == null || !context.enabled || !context.visible) {
		return null;
	}

	// if (context.result.length === 0) {
	// 	return null;
	// }

	return (
		<MentionHook
			editor={editor}
			context={context}
			editorHeight={editorHeight}
			innerRef={innerRef}
			editorInternal={editorInternal}
			inModal={inModal}
		/>
	);
}
